import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import React__default, { PureComponent, createRef, createElement } from 'react';
import PT from 'prop-types';
import styled, { css } from 'styled-components';
import { H as HelpTip } from '../HelpTip/HelpTip-151dae2e.js';
import { f as formGroupRowStyleMixin } from '../FormGroup/formGroupRowStyleMixin-b43c108e.js';
import { f as formGroupColumnStyleMixin } from '../FormGroup/formGroupColumnStyleMixin-05298d5d.js';
import { L as LabelLine } from '../FormGroup/LabelLine-e82cd306.js';
import { V as ValidationLine } from '../FormGroup/ValidationLine-31676f70.js';
import { b as inputStyles, i as inputFocusStyles } from '../InputFormGroup/InputFormGroup.styles-fffcc9e6.js';
import { i as isMobile } from '../theme/style-mixins/isMobile/isMobile-701df069.js';
import { I as InlineMessage } from '../Text/InlineMessage/InlineMessage-11bd6af9.js';
import { n as noop } from '../LocalModules/storm-ui-utils/dist/esm/noop-ea7cc233-62dc80e0.js';
import { i as isString } from '../LocalModules/storm-ui-utils/dist/esm/isString-c7dc7f9f-edb7f9ec.js';
import { c as createPseudoUID } from '../LocalModules/storm-ui-utils/dist/esm/createPseudoUID-090182c8-77d923ce.js';
import { i as isFunction } from '../LocalModules/storm-ui-utils/dist/esm/isFunction-a499fe26-1566f82d.js';
import { g as getFirstFocusableElement } from '../LocalModules/storm-ui-utils/dist/esm/focusableElements/getFirstFocusableElement-019630af-55b0ee34.js';
import { T as TaktIdConsumer } from '../TaktIdContext/TaktIdContext-f853b17a.js';
import { c as createStormTaktId } from '../TaktIdContext/TaktIdContext.utils-39f30e88.js';

const styleTextAreaMixin = /*#__PURE__*/css(["", " padding:", ";"], inputStyles, ({
  theme
}) => theme.form.textarea.padding);
const InputGroup = /*#__PURE__*/styled(({
  className,
  children,
  inputGroupRef
}) => /*#__PURE__*/React__default.createElement("div", {
  className: className,
  ref: inputGroupRef
}, children)).withConfig({
  componentId: "sc-storm-ui-30051113__sc-1ds8w60-0"
})(["background:", ";border:", ";border-color:", ";box-shadow:", ";box-sizing:border-box;border-radius:", ";color:", ";min-width:", ";display:flex;", " ", " ", " :focus-within{", "}> textarea{", "}"], ({
  theme,
  disabled
}) => disabled ? theme.form.input.disabled.bg : theme.form.input.bg, ({
  theme
}) => theme.form.input.border, ({
  theme,
  disabled
}) => disabled ? theme.form.input.disabled.borderColor : theme.form.input.borderColor, ({
  theme
}) => theme.form.input.boxShadow, ({
  theme
}) => theme.form.input.radius, ({
  theme,
  disabled
}) => disabled ? theme.form.input.disabled.color : theme.form.input.color, ({
  fullWidth,
  theme
}) => fullWidth ? 'auto' : theme.form.input.minWidth, ({
  $statusType,
  theme
}) => $statusType === 'error' && css(["border-color:", ";box-shadow:", ";"], theme.form.input.error.borderColor, theme.form.input.error.boxShadow), ({
  $statusType,
  theme
}) => $statusType === 'warning' && css(["border-color:", ";"], theme.form.input.warning.borderColor), ({
  $statusType,
  theme
}) => $statusType === 'success' && css(["border-color:", ";"], theme.form.input.success.borderColor), inputFocusStyles, styleTextAreaMixin);
InputGroup.displayName = 'InputGroup';
const TextAreaValidationMessage = /*#__PURE__*/styled(InlineMessage).withConfig({
  componentId: "sc-storm-ui-30051113__sc-1ds8w60-1"
})(["margin-top:0;margin-bottom:0;"]);
TextAreaValidationMessage.displayName = 'TextAreaValidationMessage';
const textAreaPropTypes = {
  /**
   * The page unique identifier of the TextArea input.
   */
  id: PT.string.isRequired,
  /**
   * The class name passed through to the wrapper `div`.
   */
  className: PT.string,
  /**
   * Disables the TextAreaFormGroup.
   */
  disabled: PT.bool,
  /**
   * The function called when any changes are made within the TextAreaFormGroup.
   */
  onChange: PT.func,
  /**
   * Labels are required for accessibility, but can be hidden visually.
   */
  hideLabel: PT.bool,
  /**
   * Needed for accessibility when no other label is associated to the input.
   * See "Hiding labels" above to visually hide the label.
   */
  label: PT.oneOfType([PT.string, PT.node]).isRequired,
  /**
   * Used to render content *before* the label, but outside the click-able area.
   */
  renderLabelStart: PT.oneOfType([PT.func, PT.node]),
  /**
   * Used to render content *after* the label, but outside the click-able area.
   */
  renderLabelEnd: PT.oneOfType([PT.func, PT.node]),
  /**
   * Label and message will be inlined.
   */
  inline: PT.bool,
  /**
   * Some suggested or example information.
   */
  placeholder: PT.string,
  /**
   * Adds a message below the <textarea>, which will update based on error/success/warning.
   */
  message: PT.oneOfType([PT.string, PT.func]),
  /**
   * Specifies the type of message to be displayed (error, warning, success)
   */
  statusType: PT.oneOf(['error', 'warning', 'success']),
  /**
   * Use to match container, useful for setting specific widths (by setting width on container).
   */
  fullWidth: PT.bool,
  /**
   * Use to access the <textarea> element ref.
   */
  inputRef: PT.func,
  /**
   * *Prop will be deprecated in a future version, use renderLabelEnd with `<HelpTip/>` instead.*
   * Adds an info tooltip with your help message (can pass a string or node).
   */
  help: PT.oneOfType([PT.string, PT.node]),
  /**
   * A data-id that can be used for writing unit tests.
   */
  messageE2EId: PT.string
};
const textAreaDefaultProps = {
  disabled: false,
  onChange: noop,
  hideLabel: false,
  renderLabelStart: undefined,
  renderLabelEnd: undefined,
  inline: false,
  placeholder: '',
  message: '',
  statusType: undefined,
  fullWidth: false,
  help: undefined,
  inputRef: () => null,
  messageE2EId: undefined,
  className: ''
};
class TextAreaFormGroupComponent extends PureComponent {
  constructor(props) {
    super(props);
    _defineProperty(this, "inputGroup", /*#__PURE__*/createRef());
    _defineProperty(this, "styledTextArea", void 0);
    _defineProperty(this, "setEphemeralId", () => {
      const {
        ephemeralId
      } = this.state;
      // prevent changing the ephemeralId if one was already set
      if (!isString(ephemeralId)) {
        this.setState(() => ({
          ephemeralId: createPseudoUID()
        }));
      }
    });
    _defineProperty(this, "clearEphemeralId", () => {
      this.setState(() => ({
        ephemeralId: undefined
      }));
    });
    _defineProperty(this, "renderLabelEndWithHelptip", () => {
      const {
        renderLabelEnd,
        help
      } = this.props;
      return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, isFunction(renderLabelEnd) && renderLabelEnd(), /*#__PURE__*/React__default.createElement(HelpTip, {
        position: "right"
      }, help));
    });
    _defineProperty(this, "focusInput", () => {
      if (this.inputGroup.current) {
        const focusable = getFirstFocusableElement(this.inputGroup.current);
        if (focusable instanceof HTMLElement && isFunction(focusable.focus)) {
          focusable.focus();
        }
      }
    });
    this.state = {
      ephemeralId: undefined
    };

    /*
     *  Saving a reference to the instance of the function that creates the input element at
     *  constructor time is a performance consideration. We don't want to instantiate a new
     *  render-able every time we run `render()`.
     */
    this.styledTextArea = styled(({
      /*
       * Exclude the props that are only passed to the style. If The props were passed to the
       * react node, they would be attached to the DOM node and throw a warning.
       */
      prefix,
      suffix,
      inputRef,
      ...inputProps
    }) => /*#__PURE__*/createElement('textarea', {
      ...inputProps,
      ref: inputRef
    }, inputProps.children)).withConfig({
      componentId: "sc-storm-ui-30051113__sc-1ds8w60-2"
    })(["", ""], styleTextAreaMixin);
    this.styledTextArea.displayName = 'TextArea';
  }
  componentDidMount() {
    const {
      id
    } = this.props;

    /*
     * create a ephemeral message Id after mounted without a ID and the validation line has
     * been created
     */
    if (!isString(id)) {
      this.setEphemeralId();
    }
  }
  componentDidUpdate(prevProps) {
    const {
      id
    } = this.props;

    /*
     * Deals with the edge case when a id prop is set or cleared when the validation line is
     * visible
     */
    if (id !== prevProps.id) {
      if (!isString(id)) {
        this.setEphemeralId();
      } else {
        this.clearEphemeralId();
      }
    }
  }
  render() {
    const {
      id,
      className,
      disabled,
      label,
      message,
      messageE2EId,
      statusType,
      fullWidth,
      inline,
      hideLabel,
      renderLabelStart,
      renderLabelEnd,
      inputRef,
      help,
      value,
      taktId,
      taktValue,
      ...rest
    } = this.props;
    const {
      ephemeralId
    } = this.state;
    const labelId = isString(id) ? undefined : `${ephemeralId}-textarea-label`;
    const messageId = isString(id) ? `${id}-textarea-message` : `${ephemeralId}-textarea-message`;
    return /*#__PURE__*/React__default.createElement(TaktIdConsumer, {
      taktId: taktId,
      taktValue: taktValue,
      fallbackId: createStormTaktId('text-area-form-group')
    }, ({
      getDataTaktAttributes
    }) => /*#__PURE__*/React__default.createElement("div", {
      className: className
    }, label && /*#__PURE__*/React__default.createElement(LabelLine, _extends({
      hidden: hideLabel
    }, getDataTaktAttributes({
      taktIdSuffix: 'label'
    }), {
      renderLabelStart: renderLabelStart,
      renderLabelEnd:
      /**
       * Support the `help` prop by folding it into renderLabelEnd
       */
      help ? this.renderLabelEndWithHelptip : renderLabelEnd
    }, isString(id) ? {
      labelFor: id
    } : {
      labelId,
      onLabelClick: this.focusInput
    }), label), /*#__PURE__*/React__default.createElement(InputGroup, {
      disabled: disabled,
      fullWidth: fullWidth,
      inline: inline,
      $statusType: statusType,
      inputGroupRef: this.inputGroup
    },
    /*#__PURE__*/
    /*
    * Using the non-JSX `React.createElement()` lets us choose between the named DOM
    * element and a function that returns a node, without adding additional flow control.
    */
    createElement(this.styledTextArea, {
      ...getDataTaktAttributes(),
      ...rest,
      /**
      * When `id` prop is provided, we do not need to do the labelledby work around
      */
      ...(isString(id) ? {
        id
      } : {}),
      ...(isString(labelId) ? {
        'aria-labelledby': labelId
      } : {}),
      /**
       * Add a 'aria-describedby' when any message is applied to the textarea
       */
      ...(message ? {
        'aria-describedby': messageId
      } : {}),
      /*
      * Error get a `'aria-invalid="true"`
      */
      ...(statusType === 'error' ? {
        'aria-invalid': true
      } : {}),
      value,
      inputRef,
      disabled,
      $statusType: statusType
    }, null /* children are null when assuming a closed tag like `<TextArea />` */)), !!message && /*#__PURE__*/React__default.createElement(ValidationLine, _extends({
      role: "alert",
      "aria-live": "assertive",
      id: messageId
    }, getDataTaktAttributes({
      taktIdSuffix: 'message'
    })), typeof message === 'function' ? message(value) : /*#__PURE__*/React__default.createElement(TextAreaValidationMessage, {
      messageType: statusType,
      message: message,
      "data-e2e-id": messageE2EId
    }))));
  }
}
_defineProperty(TextAreaFormGroupComponent, "propTypes", textAreaPropTypes);
_defineProperty(TextAreaFormGroupComponent, "defaultProps", textAreaDefaultProps);
const TextAreaFormGroup = /*#__PURE__*/styled(TextAreaFormGroupComponent).withConfig({
  componentId: "sc-storm-ui-30051113__sc-1ds8w60-3"
})(["", " ", ""], ({
  inline
}) => inline ? formGroupRowStyleMixin : formGroupColumnStyleMixin, isMobile(formGroupColumnStyleMixin));
TextAreaFormGroup.propTypes = textAreaPropTypes;
TextAreaFormGroup.defaultProps = textAreaDefaultProps;
TextAreaFormGroup.displayName = 'TextAreaFormGroup';
var TextAreaFormGroup$1 = TextAreaFormGroup;

export { TextAreaFormGroup$1 as T, styleTextAreaMixin as s };
//# sourceMappingURL=TextAreaFormGroup-4cc84dec.js.map
